import { useCallback, useEffect, useState } from "react";
import { GearIcon, XIcon } from "./Icons";

/**
 * Who has been using the tool.
 *
 * It exists to answer one question — is this app carrying enough of the work to
 * justify dropping Acrobat licences — so it shows counts per person, not a
 * trail of what anybody did to which file. There is nothing more to show: the
 * server never receives a filename.
 */

interface UsageRow {
  userName: string;
  userEmail: string;
  action: string;
  events: number;
  quantity: number;
  lastAt: string;
}

interface AdminRow {
  email: string;
  addedBy: string;
  addedAt: string;
}

interface ActivityRow {
  id: string;
  at: string;
  userName: string;
  userEmail: string;
  action: string;
  quantity: number;
}

interface Person {
  name: string;
  email: string;
}

/** Plain English for the stored action names. */
const LABELS: Record<string, string> = {
  sign_in: "Signed in",
  import_pdf: "Added a PDF",
  import_office: "Added an Office file",
  import_image: "Added an image",
  export_pdf: "Exported a PDF",
  convert_office: "Converted to PDF",
  delete_page: "Deleted a page",
  rotate_page: "Rotated a page",
  reorder_page: "Reordered a page",
  add_blank_page: "Added a blank page",
  split: "Split a document",
  add_text: "Added text",
  stamp: "Added numbers or a watermark",
};

const PAGE_SIZES = [25, 50, 100, 250] as const;

const RANGES = [
  { label: "Last 7 days", days: 7 },
  { label: "Last 30 days", days: 30 },
  { label: "Last 90 days", days: 90 },
  { label: "Last 12 months", days: 365 },
] as const;

function isoDaysAgo(days: number): string {
  return new Date(Date.now() - days * 24 * 60 * 60_000).toISOString();
}

/** A run of the same action by the same person, shown as one line. */
interface LogGroup {
  id: string;
  at: string;
  oldestAt: string;
  userName: string;
  userEmail: string;
  action: string;
  quantity: number;
  events: number;
}

/**
 * Adjacent events far enough apart to be separate occasions rather than one.
 *
 * Twelve blank pages added in the same minute is one thing someone did; two an
 * hour apart are two, and collapsing those would hide the gap.
 */
const COLLAPSE_WITHIN_MS = 10 * 60_000;

/**
 * Folds runs of the same action by the same person into a single line.
 *
 * Clicking "Blank page" twelve times filled the log with twelve identical rows
 * and pushed everything else out of view. Quantities are summed, so the line
 * says how many pages were affected rather than how many clicks it took.
 *
 * Done per page, so a run split across a page boundary shows on both — which
 * is what the reader is being told: those rows are on different pages.
 */
function collapse(rows: ActivityRow[]): LogGroup[] {
  const groups: LogGroup[] = [];
  for (const row of rows) {
    const last = groups[groups.length - 1];
    const sameRun =
      last !== undefined &&
      last.userEmail === row.userEmail &&
      last.action === row.action &&
      // Rows run newest first, so the previous group's oldest is the later one.
      Date.parse(last.oldestAt) - Date.parse(row.at) <= COLLAPSE_WITHIN_MS;
    if (sameRun && last) {
      last.quantity += row.quantity;
      last.events += 1;
      last.oldestAt = row.at;
    } else {
      groups.push({ ...row, oldestAt: row.at, events: 1 });
    }
  }
  return groups;
}

/** Today's events show a time; older ones need the date too. */
function formatWhen(iso: string): string {
  const at = new Date(iso);
  const time = at.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
  return at.toDateString() === new Date().toDateString()
    ? time
    : `${at.toLocaleDateString([], { month: "short", day: "numeric" })} ${time}`;
}

export function UsageDialog({ onClose }: { onClose: () => void }) {
  const [view, setView] = useState<"activity" | "summary">("activity");
  /** The admin list used to sit under the log, which meant scrolling past all of it. */
  const [settings, setSettings] = useState(false);
  const [days, setDays] = useState<number>(30);
  const [rows, setRows] = useState<UsageRow[]>([]);
  const [activity, setActivity] = useState<ActivityRow[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(50);
  /** Filters. Empty string means "everyone" / "anything". */
  const [who, setWho] = useState("");
  const [what, setWhat] = useState("");
  const [admins, setAdmins] = useState<AdminRow[]>([]);
  const [newAdmin, setNewAdmin] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      // Back out of settings first; a second Escape closes the dialog.
      if (settings) setSettings(false);
      else onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, settings]);

  /** The date range and filters, as a query string. */
  const range = useCallback(
    () =>
      new URLSearchParams({
        from: isoDaysAgo(days),
        to: new Date().toISOString(),
        ...(who ? { user: who } : {}),
        ...(what ? { action: what } : {}),
      }).toString(),
    [days, who, what],
  );

  // Changing a filter, the period or the page size goes back to the first page:
  // staying on page 7 of a set that now has two would show an empty table.
  useEffect(() => {
    setPage(0);
  }, [days, who, what, perPage]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const paged = `${range()}&limit=${perPage}&offset=${page * perPage}`;
      const [activityRes, reportRes, adminRes] = await Promise.all([
        fetch(`/api/usage/activity?${paged}`, { credentials: "same-origin" }),
        fetch(`/api/usage/report?${range()}`, { credentials: "same-origin" }),
        fetch("/api/usage/admins", { credentials: "same-origin" }),
      ]);
      if (!activityRes.ok || !reportRes.ok)
        throw new Error("The report could not be loaded.");
      const log = await activityRes.json();
      setActivity(log.rows ?? []);
      setTotal(Number(log.total ?? 0));
      // The filter only knows the people it was told about, and the list is
      // only sent for a first page — so keep the old one rather than blanking it.
      if (log.people) setPeople(log.people);
      setRows((await reportRes.json()).rows ?? []);
      if (adminRes.ok) setAdmins((await adminRes.json()).admins ?? []);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "The report could not be loaded.",
      );
    } finally {
      setLoading(false);
    }
  }, [range, page, perPage]);

  useEffect(() => {
    void load();
  }, [load]);

  async function changeAdmins(run: () => Promise<Response>) {
    setError(null);
    try {
      const response = await run();
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error ?? "That did not work.");
      setAdmins(body.admins ?? []);
      setNewAdmin("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "That did not work.");
    }
  }

  // One line per person, with their actions beneath — the report reads as
  // "who is using this", which is the question it is here to answer.
  const byPerson = new Map<
    string,
    { name: string; email: string; rows: UsageRow[] }
  >();
  for (const row of rows) {
    const entry = byPerson.get(row.userEmail) ?? {
      name: row.userName,
      email: row.userEmail,
      rows: [],
    };
    entry.rows.push(row);
    byPerson.set(row.userEmail, entry);
  }
  /** Zero-based index of the last page; 0 when there is nothing to page. */
  const lastPage = Math.max(0, Math.ceil(total / perPage) - 1);

  const totals = [...byPerson.values()].sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  return (
    <div
      className="dialog-backdrop"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <div
        className="dialog usage"
        role="dialog"
        aria-modal="true"
        aria-label="Usage"
      >
        <div className="usage__corner">
          <button
            className={settings ? "icon-btn is-on" : "icon-btn"}
            onClick={() => setSettings((on) => !on)}
            title="Who can see this"
            aria-label="Who can see this"
            aria-pressed={settings}
          >
            <GearIcon />
          </button>
          <button
            className="icon-btn"
            onClick={onClose}
            aria-label="Close usage"
          >
            <XIcon />
          </button>
        </div>

        <h2>Usage</h2>
        <p className="dialog__hint">
          What people have done, and when. No filenames and no document content
          are recorded — only that an action happened.
        </p>

        {settings ? (
          <>
            <h3 className="usage__settings-title">Who can see this</h3>
            <p className="dialog__hint">
              Anyone listed here can open Usage and see everybody&rsquo;s
              activity. There has to be at least one.
            </p>
            <ul className="usage__admins">
              {admins.map((admin) => (
                <li key={admin.email}>
                  <span>{admin.email}</span>
                  <button
                    className="btn btn--sm btn--danger"
                    onClick={() =>
                      void changeAdmins(() =>
                        fetch(
                          `/api/usage/admins/${encodeURIComponent(admin.email)}`,
                          {
                            method: "DELETE",
                            credentials: "same-origin",
                          },
                        ),
                      )
                    }
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
            <form
              className="usage__add"
              onSubmit={(event) => {
                event.preventDefault();
                void changeAdmins(() =>
                  fetch("/api/usage/admins", {
                    method: "POST",
                    credentials: "same-origin",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ email: newAdmin }),
                  }),
                );
              }}
            >
              <input
                type="email"
                value={newAdmin}
                onChange={(event) => setNewAdmin(event.target.value)}
                placeholder="name@onservices.com"
                aria-label="Email address to add as an admin"
              />
              <button
                className="btn btn--sm"
                type="submit"
                disabled={!newAdmin.trim()}
              >
                Add
              </button>
            </form>
            {error ? <p className="usage__error">{error}</p> : null}
            <button
              className="btn btn--sm usage__back"
              onClick={() => setSettings(false)}
            >
              Back to usage
            </button>
          </>
        ) : (
          <>
            <div className="usage__tabs">
              <button
                className={
                  view === "activity"
                    ? "btn btn--sm btn--active"
                    : "btn btn--sm"
                }
                onClick={() => setView("activity")}
              >
                Activity
              </button>
              <button
                className={
                  view === "summary" ? "btn btn--sm btn--active" : "btn btn--sm"
                }
                onClick={() => setView("summary")}
              >
                Totals
              </button>
            </div>

            <div className="usage__range">
              {RANGES.map((option) => (
                <button
                  key={option.days}
                  className={
                    days === option.days
                      ? "btn btn--sm btn--active"
                      : "btn btn--sm"
                  }
                  onClick={() => setDays(option.days)}
                >
                  {option.label}
                </button>
              ))}
            </div>

            <div className="usage__filters">
              <label>
                Who
                <select
                  value={who}
                  onChange={(event) => setWho(event.target.value)}
                >
                  <option value="">Everyone</option>
                  {people.map((person) => (
                    <option key={person.email} value={person.email}>
                      {person.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                What
                <select
                  value={what}
                  onChange={(event) => setWhat(event.target.value)}
                >
                  <option value="">Anything</option>
                  {Object.entries(LABELS).map(([action, label]) => (
                    <option key={action} value={action}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              {who || what ? (
                <button
                  className="btn btn--sm"
                  onClick={() => {
                    setWho("");
                    setWhat("");
                  }}
                >
                  Clear
                </button>
              ) : null}
            </div>

            {error ? <p className="usage__error">{error}</p> : null}

            {loading ? (
              <p className="dialog__hint">Loading…</p>
            ) : view === "activity" ? (
              activity.length === 0 ? (
                <p className="dialog__hint">Nothing matches.</p>
              ) : (
                <>
                  <ol className="usage__log">
                    {collapse(activity).map((row) => (
                      <li key={row.id}>
                        <span className="usage__log-when">
                          {formatWhen(row.at)}
                        </span>
                        <span className="usage__log-who">{row.userName}</span>
                        <span
                          className="usage__log-what"
                          title={
                            row.events > 1
                              ? `${row.events} actions between ${formatWhen(row.oldestAt)} and ${formatWhen(row.at)}`
                              : undefined
                          }
                        >
                          {LABELS[row.action] ?? row.action}
                          {row.quantity > 1 ? <em> ×{row.quantity}</em> : null}
                        </span>
                      </li>
                    ))}
                  </ol>
                  <div className="usage__pager">
                    <span className="usage__pager-count">
                      {(page * perPage + 1).toLocaleString()}–
                      {Math.min(total, (page + 1) * perPage).toLocaleString()}{" "}
                      of {total.toLocaleString()}
                    </span>
                    <label className="usage__pager-size">
                      Per page
                      <select
                        value={perPage}
                        onChange={(event) =>
                          setPerPage(Number(event.target.value))
                        }
                      >
                        {PAGE_SIZES.map((size) => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                      </select>
                    </label>
                    <div className="usage__pager-steps">
                      <button
                        className="btn btn--sm"
                        onClick={() => setPage(0)}
                        disabled={page === 0}
                        aria-label="First page"
                        title="First page"
                      >
                        «
                      </button>
                      <button
                        className="btn btn--sm"
                        onClick={() =>
                          setPage((current) => Math.max(0, current - 1))
                        }
                        disabled={page === 0}
                      >
                        Previous
                      </button>
                      <span className="usage__pager-page">
                        {page + 1} of {lastPage + 1}
                      </span>
                      <button
                        className="btn btn--sm"
                        onClick={() =>
                          setPage((current) => Math.min(lastPage, current + 1))
                        }
                        disabled={page >= lastPage}
                      >
                        Next
                      </button>
                      <button
                        className="btn btn--sm"
                        onClick={() => setPage(lastPage)}
                        disabled={page >= lastPage}
                        aria-label="Last page"
                        title="Last page"
                      >
                        »
                      </button>
                    </div>
                  </div>
                </>
              )
            ) : byPerson.size === 0 ? (
              <p className="dialog__hint">Nothing recorded in this period.</p>
            ) : (
              <div className="usage__people">
                {totals.map((person) => (
                  <div className="usage__person" key={person.email}>
                    <div className="usage__who">
                      <strong>{person.name}</strong>
                      <span>{person.email}</span>
                    </div>
                    <table className="usage__table">
                      <tbody>
                        {person.rows
                          .sort(
                            (a, b) =>
                              Date.parse(b.lastAt) - Date.parse(a.lastAt),
                          )
                          .map((row) => (
                            <tr key={row.action}>
                              <td>{LABELS[row.action] ?? row.action}</td>
                              <td className="usage__count">
                                {row.quantity.toLocaleString()}
                              </td>
                              <td className="usage__when">
                                {new Date(row.lastAt).toLocaleDateString()}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
